import { useEffect, useMemo, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import StepHeader from '@components/step-header/StepHeader';
import AddSupportingDocumentModal from '@forms/components/modals/AddSupportDocumentModal';
import EditSupportingDocsTableCell from '@src/app-components/table/tableCellComponents/EditSupportingDocsTableCell';
import SelectInput from '@components/select/Select';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { mdiCloseOctagon, mdiDownload, mdiPlusBox } from '@mdi/js';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { ErrorMessages, RequestMap } from '@src/utils/enums';
import TanStackTableBasic from '@src/app-components/table/TanStackTable/TanStackTableBasic';
import { useRequest, useModal } from '@stores';

export const SupportingInformationMetadata = {
  sectionName: 'Supporting Information',
};

const schema = yup.object().shape({
  hasSupportingDocuments: yup.string().required('Please select an option').oneOf(['true', 'false'], 'Invalid option'),
  tableError: yup.string().nullable(),
});

const SupportingInformation = connect(
  'doDeleteFile',
  'doDownloadFile',
  'doDownloadZip',
  'doResetFileSection',
  'selectSupportingDocFiles',
  'selectRequestAPIData',
  'selectIsAnonymous',
  ({
    doDeleteFile,
    doDownloadFile,
    doDownloadZip,
    doResetFileSection,
    supportingDocFiles,
    requestAPIData,
    isAnonymous,
    componentID,
    subtext,
    stepNo,
    updateRequestData,
  }) => {
    const { openModal } = useModal();
    const { getSelectedRequestId, isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const requestType = RequestMap[Number(getSelectedRequestId())];
    const savedHasSupportingDocuments =
      requestAPIData?.request?.[requestType]?.[0]?.hasSupportingDocuments?.toString() ?? null;

    const defaultValues = useMemo(
      () => ({
        hasSupportingDocuments: savedHasSupportingDocuments,
        supportingDocuments: null,
      }),
      [savedHasSupportingDocuments]
    );

    const methods = useForm({
      resolver: yupResolver(schema),
      mode: 'onBlur',
      defaultValues,
    });

    const {
      formState: { errors, dirtyFields },
      watch,
      trigger,
      setError,
      clearErrors,
      register,
      reset,
    } = methods;

    useEffect(() => {
      register('supportingDocuments');
    }, [register]);

    const [supportingDocsDirty, setSupportingDocsDirty] = useState(false);
    const isDirty = Object.keys(dirtyFields).length > 0 || supportingDocsDirty;
    const isTouched = isSectionTouched(stepNo);
    const errorCount = Object.keys(errors).length;
    const isValid = errorCount === 0;

    const supportingDocFileCount = supportingDocFiles?.length ?? 0;
    const hasSupportingDocuments = watch('hasSupportingDocuments');

    const hasNwp27Selected = useMemo(() => {
      const gpTypes = requestAPIData?.request?.permits?.[0]?.gpPermitTypes ?? [];
      return gpTypes.some((t) => (t?.value ?? '').toLowerCase().includes('nwp 27'));
    }, [requestAPIData]);

    const hasNwp27ReportRow = useMemo(() => {
      return (supportingDocFiles ?? []).some((f) => (f?.doctype ?? '').trim().toLowerCase() === 'nwp 27 report');
    }, [supportingDocFiles]);

    const needsNwp27Report = hasSupportingDocuments === 'true' && hasNwp27Selected && !hasNwp27ReportRow;
    const needsAtLeastOneDoc = hasSupportingDocuments === 'true' && supportingDocFileCount < 1 && !hasNwp27Selected;

    const columnHelper = createColumnHelper();

    const columnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <EditSupportingDocsTableCell
              row={row}
              doDownloadFile={doDownloadFile}
              doDeleteFile={doDeleteFile}
              isReadOnly={isReadOnly}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
          },
        }),
        columnHelper.accessor('fileName', {
          header: 'File Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('doclabel', {
          header: 'Document Label',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 400,
        }),
        columnHelper.accessor('doctype', {
          header: 'Document Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('doccreateddate', {
          header: 'Document Created Date',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 100,
        }),
        columnHelper.accessor('doccitation', {
          header: 'Information Source/Citation',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 400,
        }),
      ],
      [columnHelper, doDeleteFile, doDownloadFile, isReadOnly]
    );

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    useEffect(() => {
      updateSection(stepNo, {
        valid: isReadOnly ? true : isValid,
        label: SupportingInformationMetadata.sectionName,
      });
    }, [isReadOnly, isValid, stepNo, updateSection]);

    // Reset form values after save based on saved API state
    useEffect(() => {
      // Reset form values only for non-anonymous. No saves between sections for anonymous forms
      !isAnonymous &&
        reset(defaultValues, {
          keepDirty: true,
          keepErrors: true,
        });
      setSupportingDocsDirty(false);
      if (isTouched) {
        trigger('hasSupportingDocuments');
      }
    }, [isAnonymous, defaultValues, isTouched, reset, trigger]);

    //Trigger errors when section has been touched and hasSupportingDocuments is set to true, but no documents have been uploaded
    useEffect(() => {
      if (!isTouched) return;
      if (needsNwp27Report) {
        setError('supportingDocuments', {
          type: 'custom',
          message: 'Supporting Documents: NWP 27 Report is required for NWP 27.',
        });
        return;
      }
      if (needsAtLeastOneDoc) {
        setError('supportingDocuments', {
          type: 'custom',
          message: 'Supporting Documents: At least one supporting document is required',
        });
        return;
      }

      clearErrors('supportingDocuments');
    }, [isTouched, needsNwp27Report, needsAtLeastOneDoc, setError, clearErrors]);

    useEffect(() => {
      if (!hasNwp27Selected || isReadOnly) return;
      methods.setValue('hasSupportingDocuments', 'true', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      trigger('hasSupportingDocuments');
      updateRequestData({ hasSupportingDocuments: 'true' });
    }, [hasNwp27Selected, isReadOnly, methods, trigger, updateRequestData]);

    const handleAddClick = () => openModal(AddSupportingDocumentModal, { setSupportingDocsDirty, componentID });

    const handleOnChange = (e) => {
      updateRequestData({ hasSupportingDocuments: e.target.value });
      setSupportingDocsDirty(true);
    };

    const hasSomeS3Files = supportingDocFiles.filter((item) => item.isUploadedToS3).length !== 0;
    const hasSomeLocalFiles = supportingDocFiles.filter((item) => !item.isUploadedToS3).length !== 0;
    const hasAllS3Files = supportingDocFiles.filter((item) => !item.isUploadedToS3).length === 0;

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader
          text='Supporting Information'
          subtext={
            <>
              {subtext}
              <p>
                Please ensure the values for uploaded files only include letters, digits, white-space, and the
                characters, '_', '.', '/', '=', '+', '-', ':', '@'. If any characters outside of these mentioned are
                included in the File name or Document Label fields you will receive an error message. To resolve the
                error, remove the uploaded documents with the "Clear Staged Documents" button, and then reupload the
                files again ensuring the file name and document labels only include the aforementioned characters.
              </p>
              <p className='text-bold'>{ErrorMessages.UploadNotice}</p>
            </>
          }
        />
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='hasSupportingDocuments'
            label='Are there any supporting documents you will be uploading?'
            required
            readOnly={isReadOnly || hasNwp27Selected}
            onChange={handleOnChange}
          >
            <option key='1' value='true'>
              Yes
            </option>
            <option key='2' value='false'>
              No
            </option>
          </SelectInput>
        </div>
        {hasSupportingDocuments === 'true' && (
          <>
            <div className='table-button-container'>
              {supportingDocFiles.length !== 0 && (
                <Button
                  className='add-document-button'
                  title='Add New Row'
                  size='small'
                  onClick={() =>
                    openModal(AddSupportingDocumentModal, {
                      setSupportingDocsDirty,
                      componentID: componentID,
                    })
                  }
                  disabled={isReadOnly}
                >
                  <Icon focusable={false} path={mdiPlusBox} size='16px' />
                  Add New Row
                </Button>
              )}
              {hasSomeS3Files && (
                <Button
                  className='all-documents-button'
                  title='Download All Documents'
                  size='small'
                  onClick={() => {
                    const downloadFileNames = supportingDocFiles.reduce((acc, item) => {
                      if (item.isUploadedToS3) {
                        acc.push(item.fileName);
                      }
                      return acc;
                    }, []);
                    doDownloadZip(downloadFileNames);
                  }}
                  disabled={isReadOnly}
                >
                  <Icon focusable={false} path={mdiDownload} size='16px' />
                  Download All Documents
                </Button>
              )}
              {hasSomeLocalFiles && !isReadOnly && (
                <Button
                  className={`clear-table-button ${hasAllS3Files || isReadOnly ? 'disabled' : 'hover'}`}
                  title='Remove Rows'
                  size='small'
                  onClick={() => doResetFileSection('Supporting Documents')}
                  disabled={hasAllS3Files || isReadOnly}
                >
                  <Icon focusable={false} path={mdiCloseOctagon} size='16px' />
                  Remove Rows
                </Button>
              )}
            </div>
            <div className='margin-bottom-2' id='supportingDocuments'>
              <TanStackTableBasic
                data={supportingDocFiles}
                columns={columnDefs}
                placeholderText='No supporting documents found.'
                onAddClick={handleAddClick}
                isReadOnly={isReadOnly}
              />
            </div>
          </>
        )}
      </FormProvider>
    );
  }
);
SupportingInformation.metadata = SupportingInformationMetadata;

export default SupportingInformation;
